const A11Y_STORAGE_KEY = 'a11y-preferences';
const HIGH_CONTRAST_KEY = 'high-contrast';
const FONT_STEP_KEY = 'font-step';

/** Font-size steps: each step adjusts the root font size by this many px relative to the browser default (16px base). */
const FONT_STEPS = [-2, -1, 0, 1, 2] as const;
const FONT_STEP_DEFAULT = 2; // index of step 0 (no change) — also the minimum allowed
const FONT_STEP_MIN = FONT_STEP_DEFAULT; // user cannot decrease below the default
const FONT_STEP_MAX = FONT_STEPS.length - 1;

/**
 * Read stored preferences, apply them immediately to avoid a flash,
 * then wire up the toggle button.
 */
export const initA11yBar = (): void => {
	const toggle = document.getElementById('a11y-contrast-toggle') as HTMLButtonElement | null;
	const btnDecrease = document.getElementById('a11y-font-decrease') as HTMLButtonElement | null;
	const btnReset = document.getElementById('a11y-font-reset') as HTMLButtonElement | null;
	const btnIncrease = document.getElementById('a11y-font-increase') as HTMLButtonElement | null;

	// ── Shared helpers ────────────────────────────────────────────────────────

	const getPrefs = (): Record<string, unknown> => {
		try {
			return JSON.parse(localStorage.getItem(A11Y_STORAGE_KEY) ?? '{}');
		} catch {
			return {};
		}
	};

	const savePrefs = (prefs: Record<string, unknown>): void => {
		localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(prefs));
	};

	// ── High contrast ─────────────────────────────────────────────────────────

	if (toggle) {
		const applyHighContrast = (enabled: boolean): void => {
			if (enabled) {
				document.documentElement.dataset.a11y = 'high-contrast';
			} else {
				delete document.documentElement.dataset.a11y;
			}
			toggle.setAttribute('aria-pressed', String(enabled));
		};

		// Restore saved preference on load
		const prefs = getPrefs();
		applyHighContrast((prefs[HIGH_CONTRAST_KEY] as boolean) ?? false);

		toggle.addEventListener('click', () => {
			const current = document.documentElement.dataset.a11y === 'high-contrast';
			const next = !current;

			applyHighContrast(next);

			const updated = getPrefs();
			updated[HIGH_CONTRAST_KEY] = next;
			savePrefs(updated);
		});
	}

	// ── Font size ─────────────────────────────────────────────────────────────

	if (btnDecrease && btnReset && btnIncrease) {
		const applyFontStep = (stepIndex: number): void => {
			const clampedIndex = Math.min(FONT_STEP_MAX, Math.max(FONT_STEP_MIN, stepIndex));
			const pxDelta = FONT_STEPS[clampedIndex];

			// Drive html font-size; all rem-based Tailwind text utilities scale.
			// Images in this theme use px or % widths, so they are unaffected.
			document.documentElement.style.setProperty('--a11y-font-scale', String(pxDelta));
			document.documentElement.dataset.fontStep = String(clampedIndex);

			// Update active indicator on buttons
			btnDecrease.dataset.active = String(clampedIndex === FONT_STEP_MIN);
			btnReset.dataset.active = String(clampedIndex === FONT_STEP_DEFAULT);
			btnIncrease.dataset.active = String(clampedIndex === FONT_STEP_MAX);

			// Enable/disable boundary buttons
			btnDecrease.disabled = clampedIndex === FONT_STEP_MIN;
			btnIncrease.disabled = clampedIndex === FONT_STEP_MAX;
		};

		const getCurrentStep = (): number => {
			const stored = getPrefs()[FONT_STEP_KEY];
			const parsed = typeof stored === 'number' ? stored : FONT_STEP_DEFAULT;
			return Math.min(FONT_STEP_MAX, Math.max(FONT_STEP_MIN, parsed));
		};

		// Restore saved preference on load
		applyFontStep(getCurrentStep());

		btnDecrease.addEventListener('click', () => {
			const next = getCurrentStep() - 1;
			applyFontStep(next);
			const updated = getPrefs();
			updated[FONT_STEP_KEY] = Math.min(FONT_STEP_MAX, Math.max(FONT_STEP_MIN, next));
			savePrefs(updated);
		});

		btnReset.addEventListener('click', () => {
			applyFontStep(FONT_STEP_DEFAULT);
			const updated = getPrefs();
			updated[FONT_STEP_KEY] = FONT_STEP_DEFAULT;
			savePrefs(updated);
		});

		btnIncrease.addEventListener('click', () => {
			const next = getCurrentStep() + 1;
			applyFontStep(next);
			const updated = getPrefs();
			updated[FONT_STEP_KEY] = Math.min(FONT_STEP_MAX, Math.max(FONT_STEP_MIN, next));
			savePrefs(updated);
		});
	}
};
